// src/features/dashboard/services/Employee/employeeIdleTime.service.ts

import api from '../../../../api/apiService';
import { ENDPOINTS } from '../../../../api/endpoints';

import type {
  EmployeeIdleTimeRequest,
  EmployeeIdleKpiResponse,
  IdleMinuteByHourResponse,
  WTDIdleTrendResponse,
  IdleVsTeamResponse,
  IdleDailyDetailResponse,
} from '../../types/Employee/employeeIdleTime.types';

/**
 * Creates the common request payload used by Employee Idle Time APIs.
 */
const createDateRequest = (request: EmployeeIdleTimeRequest) => ({
  employeeCode: request.employeeCode,
  date: request.date,
});

/**
 * Fetches the employee idle-time KPI cards.
 */
export const getEmployeeIdleKPIs = async (
  request: EmployeeIdleTimeRequest,
): Promise<EmployeeIdleKpiResponse> => {
  const { data } = await api.post<EmployeeIdleKpiResponse>(
    ENDPOINTS.EMPLOYEE_IDLE_KPI,
    createDateRequest(request),
  );

  return data;
};

/**
 * Fetches hourly idle-minute chart data.
 */
export const getIdleMinutesChart = async (
  request: EmployeeIdleTimeRequest,
): Promise<IdleMinuteByHourResponse[]> => {
  const { data } = await api.post<IdleMinuteByHourResponse[]>(
    ENDPOINTS.EMPLOYEE_IDLE_HOURLY,
    createDateRequest(request),
  );

  return data;
};

/**
 * Fetches the week-to-date idle trend.
 */
export const getIdleWTDTrend = async (
  request: EmployeeIdleTimeRequest,
): Promise<WTDIdleTrendResponse[]> => {
  const { data } = await api.post<WTDIdleTrendResponse[]>(
    ENDPOINTS.EMPLOYEE_IDLE_WTD_TREND,
    createDateRequest(request),
  );

  return data;
};

/**
 * Fetches employee-versus-team idle comparison.
 * This endpoint expects `currentDate` rather than `date`.
 */
export const getIdleTeamComparison = async (
  request: EmployeeIdleTimeRequest,
): Promise<IdleVsTeamResponse> => {
  const { data } = await api.post<IdleVsTeamResponse>(
    ENDPOINTS.EMPLOYEE_IDLE_ME_VS_TEAM,
    {
      employeeCode: request.employeeCode,
      currentDate: request.date,
    },
  );

  return data;
};

/**
 * Fetches daily idle-time details.
 */
export const getIdleDailyDetails = async (
  request: EmployeeIdleTimeRequest,
): Promise<IdleDailyDetailResponse> => {
  const { data } = await api.post<IdleDailyDetailResponse>(
    ENDPOINTS.EMPLOYEE_IDLE_DAILY,
    createDateRequest(request),
  );

  return data;
};
